import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Activity, Search, FileClock, Eye, EyeOff } from "lucide-react";
import api, { fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { ExportButton } from "../components/ExportButton";

const FIELD_LABELS = {
  nome: "Nome", name: "Nome", cognome: "Cognome", sesso: "Sesso", data_nascita: "Data di nascita", luogo_nascita: "Luogo di nascita",
  codice_fiscale: "Codice fiscale", telefono: "Telefono", email: "Email", indirizzo: "Indirizzo", comune: "Comune",
  note: "Note", camera: "Camera/zona", letto: "Letto", stanza: "Stanza", reparto: "Reparto", role: "Ruolo",
  data_ingresso: "Data ingresso", data_dimissione: "Data dimissione", data_uscita: "Data uscita",
  diagnosi: "Diagnosi", allergie: "Allergie", dieta: "Dieta", mobilita: "Mobilità", terapia: "Terapia",
  medico: "Medico", referente: "Referente", parentela: "Parentela",
  retta_prevista: "Retta prevista", retta_pagata: "Retta pagata", retta: "Retta", acconto: "Acconto", saldo: "Saldo",
  importo: "Importo", pagato: "Pagato", metodo: "Metodo di pagamento", scadenza: "Scadenza", iva: "IVA",
  categoria: "Categoria", fornitore: "Fornitore", descrizione: "Descrizione", causale: "Causale",
  data: "Data", ora: "Ora", mese: "Mese", anno: "Anno", ultimo_mese: "Ultimo mese", dal_mese: "Dal mese",
  turno: "Turno", qualifica: "Qualifica", ruolo: "Ruolo", leader: "Leader", tipo: "Tipo", stato: "Stato", esito: "Esito",
  temperatura: "Temperatura", quantita: "Quantità", azione: "Azione", testo: "Testo", titolo: "Titolo",
  ore: "Ore", ore_contratto: "Ore contratto", straordinari: "Straordinari", attivo: "Attivo",
};
const prettyKey = (k) => FIELD_LABELS[k] || k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const fmtVal = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Sì" : "No";
  if (typeof v === "object") return Array.isArray(v) ? `${v.length} voci` : "…";
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(v)) { const d = new Date(v); if (!isNaN(d)) return d.toLocaleString("it-IT"); }
  return String(v);
};
const SKIP_FIELDS = new Set(["id", "_id", "company_id", "created_at", "updated_at", "created_by", "updated_by", "status", "operatore_id", "by", "operatore_nome", "device", "device_model", "user_agent", "ip", "permissions", "structure_ids"]);
const isTechKey = (k) => SKIP_FIELDS.has(k) || k.endsWith("_id") || k.endsWith("_ids") || k.startsWith("_") || /pass|pwd|pin|token|hash|secret/i.test(k);
const diffFields = (before, after) => {
  const keys = new Set([...Object.keys(before || {}), ...Object.keys(after || {})]);
  const out = [];
  keys.forEach((k) => {
    if (isTechKey(k)) return;
    const bv = fmtVal(before ? before[k] : undefined);
    const av = fmtVal(after ? after[k] : undefined);
    if (bv !== av) out.push({ k, bv, av });
  });
  return out;
};
const changesText = (r) => diffFields(r.before, r.after).map((c) => `${prettyKey(c.k)}: ${c.bv} → ${c.av}`).join("; ");

const ymd = (d) => { const x = new Date(d); return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`; };
const firstOfMonth = (ym) => `${ym}-01`;
const lastOfMonth = (ym) => { const [y, m] = ym.split("-").map(Number); return `${ym}-${String(new Date(y, m, 0).getDate()).padStart(2, "0")}`; };
const TIPO_COLOR = { "Parametri vitali": "bg-rose-100 text-rose-700", "Caduta": "bg-red-100 text-red-700", "Doccia": "bg-blue-100 text-blue-700", "Febbre/infezione": "bg-orange-100 text-orange-700", "Alvo": "bg-emerald-100 text-emerald-700", "Pannoloni": "bg-violet-100 text-violet-700", "Consegna": "bg-slate-100 text-slate-700", "Firma": "bg-teal-100 text-teal-700" };
const AZIONE_COLOR = { create: "bg-emerald-100 text-emerald-700", update: "bg-amber-100 text-amber-700", delete: "bg-red-100 text-red-700", restore: "bg-blue-100 text-blue-700" };
const AZIONE_LABEL = { create: "Creazione", update: "Modifica", delete: "Eliminazione", restore: "Ripristino" };
const hhmm = (ts) => { const m = String(ts || "").match(/(\d{2}:\d{2})/); if (m) return m[1]; const d = new Date(ts); return isNaN(d) ? "" : `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`; };
const fmtDur = (m) => { const h = Math.floor(m / 60); const mm = m % 60; return h > 0 ? `${h}h ${mm}m` : `${mm}m`; };

// PIN oscurato: visibile solo al click (privacy quando si mostra l'elenco a tutti)
const PinReveal = ({ pin }) => {
  const [show, setShow] = useState(false);
  if (!pin) return null;
  return (
    <button type="button" onClick={(e) => { e.stopPropagation(); setShow((v) => !v); }}
      className="text-[11px] text-muted-foreground inline-flex items-center gap-1 hover:text-foreground" data-testid="pin-reveal" title={show ? "Nascondi PIN" : "Mostra PIN"}>
      PIN: <b className="text-foreground tabnum">{show ? pin : "••••"}</b>
      {show ? <EyeOff size={11} /> : <Eye size={11} />}
    </button>
  );
};

// Dispositivo + IP (l'IP è un link rapido di geolocalizzazione)
const DeviceCell = ({ device, model, ip }) => {
  if (!device && !ip) return <span className="text-muted-foreground">—</span>;
  return (<>
    {device && <span className="font-semibold">{device}</span>}
    {model && <span className="block text-[11px] text-muted-foreground">{model}</span>}
    {ip && <a href={`https://ipinfo.io/${ip}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
      className="block text-[11px] text-primary hover:underline tabnum" data-testid="ip-geo-link" title="Geolocalizza IP (città/rete)">IP: {ip} ↗</a>}
  </>);
};

export default function Attivita() {
  const { selectedStructure, can, structures = [] } = useAuth();
  const canAudit = can("audit");
  const structName = (id) => (structures.find((s) => s.id === id) || {}).nome || "—";
  const todayYmd = ymd(new Date());
  const [tab, setTab] = useState("operatori");
  const [mode, setMode] = useState("giorno");
  const [day, setDay] = useState(todayYmd);
  const [month, setMonth] = useState(todayYmd.slice(0, 7));
  const [q, setQ] = useState("");
  const [fChi, setFChi] = useState("all");
  const [fTipo, setFTipo] = useState("all");
  const [fAzione, setFAzione] = useState("all");
  const [fSez, setFSez] = useState("all");
  const [fDisp, setFDisp] = useState("all");
  const [detail, setDetail] = useState(null);

  const { da, a, label } = useMemo(() => {
    if (mode === "oggi") return { da: todayYmd, a: todayYmd, label: `Oggi (${fmtDate(todayYmd)})` };
    if (mode === "mese_corrente") return { da: firstOfMonth(todayYmd.slice(0, 7)), a: todayYmd, label: "Da inizio mese" };
    if (mode === "mese") return { da: firstOfMonth(month), a: lastOfMonth(month), label: `Mese ${month}` };
    return { da: day, a: day, label: fmtDate(day) };
  }, [mode, day, month, todayYmd]);

  const { data: res = { attivita: [], totale: 0, per_operatore: [], presenze: [] } } = useQuery({
    queryKey: ["attivita", selectedStructure, da, a],
    queryFn: async () => (await api.get("/admin/attivita", { params: { structure_id: selectedStructure, da, a } })).data,
  });
  const { data: audit = [] } = useQuery({
    queryKey: ["audit", da, a, selectedStructure],
    enabled: tab === "audit" && canAudit,
    queryFn: async () => (await api.get("/audit", { params: { da, a, structure_id: selectedStructure } })).data,
  });

  const term = q.trim().toLowerCase();
  const opDispList = Array.from(new Set(res.attivita.map((x) => x.device))).filter(Boolean).sort();
  const rows = res.attivita.filter((x) =>
    (fChi === "all" || x.operatore === fChi) &&
    (fTipo === "all" || x.tipo === fTipo) &&
    (fDisp === "all" || x.device === fDisp) &&
    (!term || `${x.operatore} ${x.operatore_pin || ""} ${x.tipo} ${x.ospite} ${x.dettaglio} ${x.device || ""} ${x.device_model || ""}`.toLowerCase().includes(term)));

  const auditActions = Array.from(new Set(audit.map((x) => x.action))).filter(Boolean);
  const auditWhoList = Array.from(new Set(audit.map((x) => x.operatore_nome || x.user_name))).filter(Boolean).sort();
  const auditSezList = Array.from(new Set(audit.map((x) => x.section))).filter(Boolean).sort();
  const auditDispList = Array.from(new Set(audit.map((x) => x.device))).filter(Boolean).sort();
  const auditRows = audit.filter((x) =>
    (fAzione === "all" || x.action === fAzione) &&
    (fChi === "all" || (x.operatore_nome || x.user_name) === fChi) &&
    (fSez === "all" || x.section === fSez) &&
    (fDisp === "all" || x.device === fDisp) &&
    (!term || `${x.operatore_nome || ""} ${x.operatore_pin || ""} ${x.user_name} ${x.action} ${x.section} ${x.device || ""} ${x.device_model || ""}`.toLowerCase().includes(term)));

  // Opzioni filtri condivisi "Chi" e "Dispositivo" in base alla tab attiva
  const whoOptions = tab === "operatori" ? res.per_operatore.map((p) => p.operatore) : auditWhoList;
  const dispOptions = tab === "operatori" ? opDispList : auditDispList;

  const exportCfg = tab === "operatori"
    ? { filename: `attivita_${da}_${a}`, title: "Attività operatori", subtitle: label,
        columns: [
          { header: "Data/ora", accessor: (r) => r.ts },
          { header: "Chi", accessor: (r) => r.operatore },
          { header: "PIN", accessor: (r) => r.operatore_pin || "" },
          { header: "Attività", accessor: (r) => r.tipo },
          { header: "Ospite", accessor: (r) => r.ospite || "—" },
          { header: "Dispositivo", accessor: (r) => [r.device, r.device_model].filter(Boolean).join(" · ") || "—" },
          { header: "IP", accessor: (r) => r.ip || "" },
          { header: "Dettaglio", accessor: (r) => r.dettaglio },
        ], rows }
    : { filename: `modifiche_archivi_${da}_${a}`, title: "Modifiche archivi", subtitle: label,
        columns: [
          { header: "Data/ora", accessor: (r) => new Date(r.created_at).toLocaleString("it-IT") },
          { header: "Chi", accessor: (r) => r.operatore_nome || r.user_name },
          { header: "PIN", accessor: (r) => r.operatore_pin || "" },
          { header: "Account", accessor: (r) => r.user_name },
          { header: "Struttura", accessor: (r) => structName(r.structure_id) },
          { header: "Azione", accessor: (r) => AZIONE_LABEL[r.action] || r.action },
          { header: "Sezione", accessor: (r) => r.section },
          { header: "Dispositivo", accessor: (r) => [r.device, r.device_model].filter(Boolean).join(" · ") || "—" },
          { header: "IP", accessor: (r) => r.ip || "" },
          { header: "Modifiche", accessor: (r) => changesText(r) || "—" },
        ], rows: auditRows };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-head font-bold flex items-center gap-2"><Activity size={22} className="text-primary" /> Registro attività</h1>
          <p className="text-sm text-muted-foreground">Monitora cosa fa ogni operatore e ogni account, giorno per giorno. <span className="font-semibold text-foreground">{label}</span></p>
        </div>
        <ExportButton filename={exportCfg.filename} title={exportCfg.title} subtitle={exportCfg.subtitle} columns={exportCfg.columns} rows={exportCfg.rows} testid="registro-export" />
      </div>

      {/* Barra filtri condivisa */}
      <div className="gr-card p-3 mb-4 flex flex-wrap items-center gap-2" data-testid="attivita-filtri">
        <div className="flex items-center gap-1 rounded-lg bg-secondary p-1">
          {[["oggi", "Oggi"], ["mese_corrente", "Da inizio mese"], ["mese", "Mese"], ["giorno", "Giorno"]].map(([m, lbl]) => (
            <button key={m} onClick={() => setMode(m)} data-testid={`periodo-${m}`} className={`text-xs font-semibold px-2.5 py-1.5 rounded-md transition-colors ${mode === m ? "bg-white shadow-sm text-primary" : "text-muted-foreground hover:text-foreground"}`}>{lbl}</button>
          ))}
        </div>
        {mode === "giorno" && <input type="date" value={day} onChange={(e) => setDay(e.target.value)} className="border rounded-md px-2 py-1.5 text-sm" data-testid="attivita-data" />}
        {mode === "mese" && <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="border rounded-md px-2 py-1.5 text-sm" data-testid="attivita-mese" />}

        <Select value={fChi} onValueChange={setFChi}>
          <SelectTrigger className="w-44 h-9" data-testid="filtro-chi"><SelectValue placeholder="Chi" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti (chi)</SelectItem>
            {whoOptions.map((w) => <SelectItem key={w} value={w}>{w}</SelectItem>)}
          </SelectContent>
        </Select>
        {tab === "operatori" && (
          <Select value={fTipo} onValueChange={setFTipo}>
            <SelectTrigger className="w-40 h-9" data-testid="filtro-tipo"><SelectValue placeholder="Tipo attività" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le attività</SelectItem>
              {Object.keys(TIPO_COLOR).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {tab === "audit" && (
          <>
            <Select value={fSez} onValueChange={setFSez}>
              <SelectTrigger className="w-40 h-9" data-testid="filtro-sezione"><SelectValue placeholder="Sezione" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le sezioni</SelectItem>
                {auditSezList.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={fAzione} onValueChange={setFAzione}>
              <SelectTrigger className="w-40 h-9" data-testid="filtro-azione"><SelectValue placeholder="Azione" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le azioni</SelectItem>
                {auditActions.map((act) => <SelectItem key={act} value={act}>{AZIONE_LABEL[act] || act}</SelectItem>)}
              </SelectContent>
            </Select>
          </>
        )}
        <Select value={fDisp} onValueChange={setFDisp}>
          <SelectTrigger className="w-40 h-9" data-testid="filtro-dispositivo"><SelectValue placeholder="Dispositivo" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i dispositivi</SelectItem>
            {dispOptions.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="relative ml-auto"><Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca..." className="pl-8 w-52" data-testid="attivita-search" /></div>
      </div>

      <Tabs value={tab} onValueChange={(v) => { setTab(v); setFChi("all"); setFDisp("all"); setFTipo("all"); setFSez("all"); setFAzione("all"); }}>
        <TabsList className="mb-4">
          <TabsTrigger value="operatori" data-testid="tab-operatori"><Activity size={15} className="mr-1.5" /> Attività operatori</TabsTrigger>
          {canAudit && <TabsTrigger value="audit" data-testid="tab-audit"><FileClock size={15} className="mr-1.5" /> Modifiche archivi</TabsTrigger>}
        </TabsList>

        <TabsContent value="operatori">
          <div className="flex flex-wrap gap-2 mb-4" data-testid="attivita-per-operatore">
            <span className="text-xs font-semibold text-muted-foreground self-center">{rows.length} attività ·</span>
            {res.per_operatore.map((p) => <button key={p.operatore} onClick={() => setFChi(fChi === p.operatore ? "all" : p.operatore)} className={`text-xs rounded-full px-2.5 py-1 font-semibold transition-colors ${fChi === p.operatore ? "bg-primary text-white" : "bg-secondary hover:bg-secondary/70"}`}>{p.operatore}: {p.count}</button>)}
          </div>

          {(res.presenze || []).length > 0 && (
            <div className="gr-card p-4 mb-4" data-testid="attivita-presenze">
              <div className="text-sm font-head font-bold mb-3 flex items-center gap-2"><Activity size={16} className="text-primary" /> Presenze del periodo (Firma Rapida)</div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {res.presenze.map((p) => (
                  <div key={p.operatore} className="flex items-center justify-between rounded-lg border bg-white p-3" data-testid={`presenza-${p.operatore}`}>
                    <div><div className="font-semibold text-sm">{p.operatore}</div><div className="text-xs text-muted-foreground">{p.sessioni} access{p.sessioni === 1 ? "o" : "i"}</div></div>
                    <div className="text-lg font-head font-bold tabnum text-primary">{fmtDur(p.minuti)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="gr-card overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-secondary/60 text-xs uppercase tracking-wide text-muted-foreground">
                <th className="text-left p-2 w-28">Data/ora</th><th className="text-left p-2">Chi</th><th className="text-left p-2">Attività</th><th className="text-left p-2">Ospite</th><th className="text-left p-2">Dispositivo</th><th className="text-left p-2">Dettaglio</th>
              </tr></thead>
              <tbody>
                {rows.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-muted-foreground">Nessuna attività per il periodo e i filtri selezionati</td></tr>}
                {rows.map((x, i) => (
                  <tr key={i} className={`border-t ${x.anomalo ? "bg-red-50" : ""}`} data-testid={`attivita-row-${i}`}>
                    <td className="p-2 tabnum text-muted-foreground text-xs">{da !== a ? <span>{String(x.ts).slice(0, 10)} </span> : null}{hhmm(x.ts)}</td>
                    <td className="p-2 font-semibold">{x.operatore}{x.operatore_pin && <div className="font-normal"><PinReveal pin={x.operatore_pin} /></div>}</td>
                    <td className="p-2"><span className={`text-xs font-bold px-2 py-0.5 rounded-full ${TIPO_COLOR[x.tipo] || "bg-secondary"}`}>{x.tipo}</span>{x.anomalo && <span className="ml-1.5 inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700" data-testid={`accesso-anomalo-${i}`} title="Accesso da un IP mai usato prima da questo operatore">⚠ Accesso anomalo</span>}</td>
                    <td className="p-2">{x.ospite || "—"}</td>
                    <td className="p-2 text-xs"><DeviceCell device={x.device} model={x.device_model} ip={x.ip} /></td>
                    <td className="p-2 text-muted-foreground">{x.dettaglio}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {canAudit && (
          <TabsContent value="audit">
            <p className="text-xs text-muted-foreground mb-3">{auditRows.length} modifiche agli archivi · attribuite all'<b>account</b> che ha effettuato l'operazione.</p>
            <div className="gr-card overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="bg-secondary/60 text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="text-left p-2 w-36">Data/ora</th><th className="text-left p-2">Chi</th><th className="text-left p-2">Struttura</th><th className="text-left p-2">Azione</th><th className="text-left p-2">Sezione</th><th className="text-left p-2">Dispositivo</th><th className="text-left p-2">Dettaglio</th>
                </tr></thead>
                <tbody>
                  {auditRows.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-muted-foreground">Nessuna modifica per il periodo e i filtri selezionati</td></tr>}
                  {auditRows.map((x) => {
                    const changes = diffFields(x.before, x.after);
                    return (
                    <tr key={x.id} className="border-t hover:bg-secondary/40 cursor-pointer" data-testid={`audit-${x.id}`} onClick={() => setDetail(x)}>
                      <td className="p-2 text-xs tabnum text-muted-foreground">{new Date(x.created_at).toLocaleString("it-IT")}</td>
                      <td className="p-2"><div className="font-semibold">{x.operatore_nome || x.user_name}</div>{x.operatore_pin && <PinReveal pin={x.operatore_pin} />}{x.operatore_nome && <div className="text-[11px] text-muted-foreground">account: {x.user_name}</div>}</td>
                      <td className="p-2 text-xs">{structName(x.structure_id)}</td>
                      <td className="p-2"><span className={`text-xs font-bold px-2 py-0.5 rounded-full ${AZIONE_COLOR[x.action] || "bg-secondary"}`}>{AZIONE_LABEL[x.action] || x.action}</span></td>
                      <td className="p-2 capitalize">{x.section}</td>
                      <td className="p-2 text-xs"><DeviceCell device={x.device} model={x.device_model} ip={x.ip} /></td>
                      <td className="p-2 text-xs text-primary font-semibold" data-testid={`audit-detail-link-${x.id}`}>{changes.length > 0 ? `${changes.length} camp${changes.length === 1 ? "o" : "i"} →` : "Vedi"}</td>
                    </tr>
                  ); })}
                </tbody>
              </table>
            </div>
          </TabsContent>
        )}
      </Tabs>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg" data-testid="audit-detail-dialog">
          <DialogHeader><DialogTitle>Dettaglio modifica</DialogTitle></DialogHeader>
          {detail && (() => {
            const changes = diffFields(detail.before, detail.after);
            return (
              <div className="space-y-3 text-sm">
                <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
                  <span><b className="text-foreground">{AZIONE_LABEL[detail.action] || detail.action}</b> · {detail.section}</span>
                  <span className="inline-flex items-center gap-1">Chi: <b className="text-foreground">{detail.operatore_nome || detail.user_name}</b>{detail.operatore_pin && <PinReveal pin={detail.operatore_pin} />}{detail.operatore_nome ? ` (account ${detail.user_name})` : ""}</span>
                  <span>{new Date(detail.created_at).toLocaleString("it-IT")}</span>
                  <span>Struttura: {structName(detail.structure_id)}</span>
                  {detail.device && <span>Dispositivo: <b className="text-foreground">{detail.device}</b>{detail.device_model ? ` · ${detail.device_model}` : ""}</span>}
                  {detail.ip && <span>IP: <a href={`https://ipinfo.io/${detail.ip}`} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline tabnum" title="Geolocalizza IP">{detail.ip} ↗</a></span>}
                </div>
                {changes.length === 0 ? (
                  <p className="text-muted-foreground">Nessun campo rilevante modificato.</p>
                ) : (
                  <div className="border rounded-lg divide-y" data-testid="audit-detail-changes">
                    <div className="grid grid-cols-[1fr_1fr_1fr] gap-2 px-3 py-1.5 bg-secondary/60 text-xs font-bold uppercase tracking-wide text-muted-foreground">
                      <span>Campo</span><span>Prima</span><span>Dopo</span>
                    </div>
                    {changes.map((c) => (
                      <div key={c.k} className="grid grid-cols-[1fr_1fr_1fr] gap-2 px-3 py-2">
                        <span className="font-semibold">{prettyKey(c.k)}</span>
                        <span className="text-muted-foreground line-through decoration-red-300">{c.bv}</span>
                        <span className="text-emerald-700 font-semibold">{c.av}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
